import { useLazyGetDemoQuery, useLazyGetTourQuery } from '@carboncopyco/data';
import type {
  IDemo,
  IPresentation,
  IRecording,
  Playbook,
  ScreenResolution,
  Tour,
  Workspace,
  WorkspaceFolder,
} from '@carboncopyco/types';
import {
  type GlobalTabType,
  TabGroupName,
  TabType,
} from '@carboncopyco/types/desktop';
import { useNavigate, useParams } from 'react-router-dom';
import { settings } from '../../../settings';
import { generateGroupId } from '../../util/tab-util';
import { useOpenDemoEditorTab } from '../screens/utils/openDemoEditorTab';
import useOpenDemoTab from '../screens/utils/openDemoTab';
import { useAppService } from '../services/AppService';
import { HOME_TAB_ID, useTabManager } from '../services/TabManager';
import { useClonerService } from '../services/cloner/ClonerService';
import { useTabs, useUser, useWorkspaces } from '../store/context';
import { SidebarContentType } from '../types/WorkspacesTypes';
import useEditorGroupTab from './useEditorGroupTab';
import useSwitchTab from './useSwitchTab';
import { useWorkspacesMap } from './useWorkspacesMap';

interface NavigateToDemoProps extends Pick<IDemo, 'id'> {
  removeDeleted?: boolean;
  firstUrl?: string;
  workspaceId: string;
}

export const useCustomNavigate = () => {
  const { activeOrganization } = useUser();
  const { startPreviewClone, startPreviewDemo, startClone, startNewOverlay } =
    useAppService();
  const [getDemo] = useLazyGetDemoQuery();
  const [getTour] = useLazyGetTourQuery();
  const openDemoEditorTab = useOpenDemoEditorTab();
  const { updateClone } = useClonerService();
  const { switchWorkspace, activeWorkspace } = useWorkspaces();
  const workspacesMap = useWorkspacesMap();
  const openDemoTab = useOpenDemoTab();
  const { getFirstGroupTabByGroupId } = useEditorGroupTab();
  const switchTab = useSwitchTab();
  const { createGlobalTab, closeGlobalTab } = useTabManager();
  const { setActiveGlobalTabId, setRedirectRoute, activeGlobalTab } = useTabs();
  const { updateEditorTabVisibility } = useEditorGroupTab();

  const organizationId = String(activeOrganization?.id || '');

  const navigate = useNavigate();
  const { workspaceId } = useParams<{
    workspaceId: string;
  }>();

  const workspaceBasePath = `/${organizationId}/workspace/`;

  let basePath = `${workspaceBasePath}${workspaceId || activeWorkspace.current.id}`;
  const webDefaultURL = `${settings.WEB_APP}/${organizationId}`;

  const links = {
    getAllWorkspacesLink: () => `/${organizationId}/workspaces`,
    getDemosLink: () => `${basePath}/demos`,
    getWorkspaceLink: (id: string) => `${workspaceBasePath}${id}`,
    geCreateOverlayDemoLink: () => `${basePath}/demo/new`,
    getRecorderLink: () => `${basePath}/recorder`,
    getDemoPageRoot: (id: IDemo['id']) => `${basePath}/demo/${id}`,
    getToursLink: () => `${basePath}/tours`,
    getTourPageRoot: (id: Tour['id']) => `${basePath}/tour/${id}`,
    getTourEditorLink: (id: Tour['id']) => `${basePath}/tour/${id}/editor`,
    getPresentationPageLink: (id: IPresentation['id'], tab = 'activity') =>
      `${basePath}/presentation/${id}/${tab}`,
    getTourPageLink: (id: Tour['id'], tab = 'overview') =>
      `${basePath}/tour/${id}/${tab}`,
    getTourPageLinkByWorkspace: (
      id: Tour['id'],
      wsid: Workspace['id'],
      tab = 'overview'
    ) => `${workspaceBasePath}${wsid}/tour/${id}/${tab}`,
    getDemoPageLink: (id: number, tab = 'overview') => {
      return `${basePath}/demo/${id}/${tab}`;
    },
    getClonePageRoot: (id: IRecording['id']) => `${basePath}/clone/${id}`,
    getClonePageLink: (id: IRecording['id'], tab = 'overview') =>
      `${basePath}/clone/${id}/${tab}`,
    getTourDemoCapture: (
      id: IRecording['id'],
      demoWorkspace: IRecording['workspaceId'] | null
    ) =>
      `${webDefaultURL}/workspace/${
        demoWorkspace ?? workspaceId ?? activeWorkspace.current.id
      }/demo/${id}/tour-capture`,
    getVisualEditorLink: (demoId: IDemo['id'], screenUrl?: string) =>
      `${basePath}/demo/${demoId}/dynamic-editor${screenUrl ? `?url=${screenUrl}` : ''}`,
    getDemoPageLinkByWorkspace: (
      id: IDemo['id'],
      wId: Workspace['id'],
      tab = 'overview'
    ) => `${workspaceBasePath}${wId}/demo/${id}/${tab}`,
    getPresentationTabLink: (
      id: IPresentation['id'],
      demoWorkspaceId?: string
    ) =>
      `${workspaceBasePath}${
        demoWorkspaceId || activeWorkspace || workspaceId
      }/demo/${id}/presentations`,
    getPlaybookPresentationTabLink: (
      id: Playbook['id'],
      demoWorkspaceId?: string
    ) =>
      `${workspaceBasePath}${
        demoWorkspaceId || activeWorkspace || workspaceId
      }/playbook/${id}/presentations`,
    getDemostacksTabLink: (id: IPresentation['id'], demoWorkspaceId?: string) =>
      `${workspaceBasePath}${
        demoWorkspaceId || activeWorkspace || workspaceId
      }/demo/${id}/demostacks`,
    getOrganizationSettingsLink: (tab: string) =>
      `/${organizationId}/settings/${tab}`,
    getActivitiesLink: () => `/${organizationId}/activities`,
    getFolderPageLink: (id: WorkspaceFolder['id'], wId: Workspace['id']) =>
      `${workspaceBasePath}${wId}/folder/${id}`,
    getArchivedFolderPageLink: (
      id: WorkspaceFolder['id'],
      wId: Workspace['id']
    ) => `${workspaceBasePath}${wId}/archive/folder/${id}`,
    getPlaybookPageRoot: (id: Playbook['id']) => `${basePath}/playbook/${id}`,
    getPlaybookPageLink: (
      id: Playbook['id'],
      tab = 'overview',
      innerTab = ''
    ) =>
      innerTab
        ? `${basePath}/playbook/${id}/${tab}/${innerTab}`
        : `${basePath}/playbook/${id}/${tab}`,
    getPlaybookPageLinkByWorkspace: (
      id: Playbook['id'],
      wId: Workspace['id'],
      tab = 'overview'
    ) => `${workspaceBasePath}${wId}/playbook/${id}/${tab}`,
  };

  const navigateToRoute = (route: string) => {
    navigate(route);
  };

  const setWorkspace = (wId: Workspace['id']) => {
    const workspace = workspacesMap[wId];
    if (!workspace) return;
    switchWorkspace(workspace, SidebarContentType.Workspace);
  };

  const updateBasePath = (wId?: string) => {
    if (wId && !workspaceId) basePath = `${workspaceBasePath}${wId}`;
  };

  const navigateToUpdateRecording = async (cloneId: string | number) => {
    await updateClone(organizationId, cloneId);
  };

  const navigateToStudio = (studioLink: string) => {
    window.dsDesktop.shellOpenExternal(studioLink);
  };

  const navigateToTourPreview = async (tourId: Tour['id'] | string) => {
    if (!workspaceId) {
      return;
    }

    const tour = await getTour({
      workspaceId,
      organizationId,
      id: Number(tourId),
    }).unwrap();

    const isCurrentTabEditor = activeGlobalTab.type === TabType.TOURS_EDITOR;

    if (isCurrentTabEditor) {
      updateEditorTabVisibility({
        editorTab: activeGlobalTab,
        show: false,
      });
    }

    const groupId = generateGroupId(
      Number(tour.id),
      isCurrentTabEditor ? TabGroupName.TourEditor : undefined
    );

    createGlobalTab({
      type: TabType.TOURS_PREVIEW,
      title: tour.tourName,
      payload: {
        tour,
        isOpenedFromEditor: isCurrentTabEditor,
      },
      group: {
        id: groupId,
        name: TabGroupName.TourPreview,
        isVisibleIfGroupIsActive: true,
        isVisibleIfGroupIsInactive: true,
        showNonRelatedGroupTabsIfActive: false,
        focusOnCreate: true,
        closeGroupOnClose: false,
        mergingOptions: {
          allowed: true,
          key: groupId,
          index: 0,
          isDetached: false,
          detachedTitle: tour.tourName,
        },
      },
    });
  };

  const navigateToPresentationPageFromHomepage = (
    id: number,
    tab = 'activity'
  ) => {
    navigate(`${workspaceBasePath}{wId}/presentation/${id}/${tab}`);
  };

  const navigateToNotificationsSettings = () => {
    navigate(`/${organizationId}/notifications/settings`);
  };

  const navigateToActivities = () => {
    navigate(links.getActivitiesLink());
  };

  const navigateToOrganizationSettings = (tab = 'team') => {
    navigate(links.getOrganizationSettingsLink(tab));
  };

  const navigateToTourCapture = async ({
    demoId,
    originalTourId,
    insertIndex = 0,
    folderId,
    tourEditorTabId,
    isOpenedFromEditor = false,
    screenResolution,
  }: {
    demoId: number;
    originalTourId?: number;
    insertIndex?: number;
    folderId?: string;
    tourEditorTabId?: number;
    isOpenedFromEditor?: boolean;
    screenResolution?: ScreenResolution;
  }) => {
    const { data: demo } = await getDemo({
      organizationId,
      id: String(demoId),
      removeDeleted: true,
    });

    const groupId = originalTourId
      ? generateGroupId(
          Number(originalTourId),
          isOpenedFromEditor ? TabGroupName.TourEditor : undefined
        )
      : undefined;

    if (!demo) {
      throw new Error('DEMO NOT FOUND');
    }

    openDemoTab(
      { ...demo },
      {
        tabTitle: demo.demoName,
        tabType: TabType.TOURS_CREATE,
        organizationId,
        demoId: String(demoId),
        workspaceId: demo.workspaceId,
        isOpenedFromEditor,
        tourData: {
          originalTourId,
          insertIndex,
          tourEditorTabId,
        },
        screenResolution,
        folderId,
        group: groupId
          ? {
              id: groupId,
              name: TabGroupName.TourCapture,
              isVisibleIfGroupIsActive: true,
              isVisibleIfGroupIsInactive: true,
              showNonRelatedGroupTabsIfActive: true,
              focusOnCreate: true,
              closeGroupOnClose: false,
              mergingOptions: {
                allowed: true,
                key: groupId,
                index: 0,
                isDetached: false,
                detachedTitle: '',
              },
            }
          : undefined,
      }
    );
  };

  const navigateToTourEditor = async ({
    id,
    selectedFrameIndex,
    skipVisibilityCheck = false,
    folderId,
    workspaceId,
  }: {
    id: number | string;
    selectedFrameIndex?: number;
    skipVisibilityCheck?: boolean;
    workspaceId?: string | undefined;
    folderId?: number | undefined;
  }) => {
    const editorTab =
      activeGlobalTab.group &&
      getFirstGroupTabByGroupId(activeGlobalTab.group.id);

    if (editorTab && !skipVisibilityCheck) {
      updateEditorTabVisibility({ editorTab, show: true });
      switchTab(editorTab);
      return;
    }

    const tour = await getTour({
      organizationId,
      id: Number(id),
    }).unwrap();

    const groupId = generateGroupId(tour.id, TabGroupName.TourEditor);

    const payload = {
      tour,
      source: undefined,
    };

    if (selectedFrameIndex) {
      payload.source = {
        selectedFrameIndex,
      } as any;
    }

    createGlobalTab({
      type: TabType.TOURS_EDITOR,
      title: tour.tourName,
      payload,
      organizationId,
      workspaceId: workspaceId || tour.workspaceId,
      folderId: String(folderId),
      group: {
        id: groupId,
        name: TabGroupName.TourEditor,
        isVisibleIfGroupIsActive: true,
        isVisibleIfGroupIsInactive: true,
        showNonRelatedGroupTabsIfActive: true,
        focusOnCreate: true,
        closeGroupOnClose: true,
        mergingOptions: {
          allowed: true,
          key: tour.id,
          index: 0,
          isDetached: false,
          detachedTitle: tour.tourName,
        },
      },
    });

    if (skipVisibilityCheck && editorTab) {
      closeGlobalTab(editorTab.id);
    }
  };

  const navigateToTourPage = (id: Tour['id'], tab?: string) => {
    navigate(links.getTourPageLink(id as Tour['id'], tab));
  };

  const navigateToWebIntegrationSettings = () => {
    window.dsDesktop.shellOpenExternal(
      `${webDefaultURL}/settings/integrations`
    );
  };

  const navigateToWebAnalyticsDashboard = () => {
    window.dsDesktop.shellOpenExternal(`${webDefaultURL}/dashboard`);
  };

  const navigateToWebSettings = () => {
    window.dsDesktop.shellOpenExternal(`${webDefaultURL}/settings`);
  };

  const navigateToWebTourCapture = ({
    appUrl,
    playerToken,
    fileServiceToken,
    tourId,
    originalTourId,
    insertIndex,
    folderId,
    screenResolution,
    tourEditorTabId,
    isOpenedFromEditor = false,
  }: {
    appUrl: string;
    playerToken?: string;
    fileServiceToken?: string;
    tourId?: string;
    originalTourId?: string;
    insertIndex?: number;
    folderId?: string;
    screenResolution?: ScreenResolution;
    tourEditorTabId?: number;
    isOpenedFromEditor?: boolean;
  }) => {
    const groupId = originalTourId
      ? generateGroupId(
          Number(originalTourId),
          isOpenedFromEditor ? TabGroupName.TourEditor : undefined
        )
      : undefined;

    createGlobalTab<GlobalTabType>({
      type: TabType.LIVE_TOURS_CREATE,
      url: appUrl,
      workspaceId,
      folderId,
      organizationId,
      payload: {
        isOpenedFromEditor,
        source: {
          appUrl,
          playerToken,
          fileServiceToken,
          tourId,
          originalTourId,
          insertIndex,
          tourEditorTabId,
        },
        screenResolution,
      },
      title: 'Untitled Tour',
      group: groupId
        ? {
            id: groupId,
            name: TabGroupName.TourCapture,
            isVisibleIfGroupIsActive: true,
            isVisibleIfGroupIsInactive: true,
            showNonRelatedGroupTabsIfActive: true,
            focusOnCreate: true,
            closeGroupOnClose: false,
            mergingOptions: {
              allowed: true,
              key: groupId,
              index: 0,
              isDetached: false,
              detachedTitle: '',
            },
          }
        : undefined,
      createGlobalTabOnElectron: {
        startOn: 'tab-creation',
        withInterceptor: true,
        withWebSecurity: true,
      },
    });
  };

  const navigateToTourPageByWorkspace = (
    id: Tour['id'],
    wsid: Workspace['id'],
    tab = 'overview'
  ) => {
    const redirectUrl = links.getTourPageLinkByWorkspace(id, wsid, tab);
    setActiveGlobalTabId(HOME_TAB_ID);
    setRedirectRoute(redirectUrl);
    setWorkspace(wsid);
    navigate(redirectUrl);
  };

  const navigateToPresentationPage = (
    id: IPresentation['id'],
    tab?: string
  ) => {
    navigate(links.getPresentationPageLink(id, tab));
  };

  const navigateToDemoPreview = async ({
    id,
    removeDeleted = true,
    workspaceId,
  }: NavigateToDemoProps) => {
    try {
      if (workspaceId) {
        const demo = await getDemo({
          workspaceId,
          organizationId,
          id: id.toString(),
          removeDeleted,
        }).unwrap();
        updateBasePath(workspaceId ?? demo?.workspaceId);
        startPreviewDemo(demo);
      }
    } catch (e) {
      console.error('Error navigating to demo preview', e);
    }
  };

  const navigateToDemoShares = (id: number) => {
    navigate(`${links.getDemoPageRoot(id)}/links`);
  };

  const navigateToPresentationsTab = (
    id: string | number,
    demoWorkspaceId?: string
  ) => {
    navigate(links.getPresentationTabLink(id, demoWorkspaceId));
  };

  const navigateToPlaybookPresentationsTab = (
    id: string,
    demoWorkspaceId?: string
  ) => {
    navigate(links.getPlaybookPresentationTabLink(id, demoWorkspaceId));
  };

  const navigateToDemostacksTab = (id: string, demoWorkspaceId?: string) => {
    navigate(links.getDemostacksTabLink(id, demoWorkspaceId));
  };

  const navigateToRecordingPreview = (
    recording: Pick<IRecording, 'firstUrl' | 'id' | 'playerToken'>
  ) => {
    startPreviewClone(recording as IRecording);
  };

  const navigateToEditor = async (demoId: IDemo['id']) => {
    try {
      const demo = await getDemo({
        organizationId,
        id: demoId.toString(),
        removeDeleted: true,
      }).unwrap();
      updateBasePath(demo?.workspaceId);
      openDemoEditorTab(demo, activeOrganization.id);
    } catch (e) {
      console.error('Error navigating to demo editor', e);
    }
  };

  const navigateToDemoPage = (
    id: string,
    workspaceId: string,
    tab = 'overview'
  ) => {
    setWorkspace(workspaceId);
    const ws = workspacesMap[workspaceId];
    switchWorkspace(ws);
    updateBasePath(workspaceId);
    navigate(links.getDemoPageLinkByWorkspace(Number(id), workspaceId, tab));
  };

  const navigateToDemoPageByWorkspace = (
    id: string,
    workspaceId: string,
    tab?: string
  ) => {
    navigateToDemoPage(id, workspaceId, tab);
  };

  const navigateToAllTours = () => {
    navigate(links.getToursLink());
  };

  const navigateToClonePage = (
    id: number | string,
    tab?: string,
    cloneWorkspaceId?: number | string
  ) => {
    updateBasePath(cloneWorkspaceId?.toString());
    navigate(`${links.getClonePageLink(id as IRecording['id'], tab)}`);
  };

  const navigateToClonePageByWorkspace = (
    id: string | number,
    wId: Workspace['id'],
    tab = 'overview'
  ) => {
    setWorkspace(wId);
    navigate(`${workspaceBasePath}${wId}/clone/${id}/${tab}`);
  };

  const navigateToAllDemos = () => {
    navigate(links.getDemosLink());
  };

  const navigateToWorkspaces = () => {
    switchWorkspace(null, SidebarContentType.MyWorkspaces);
    return navigate(`/${organizationId}/workspaces`);
  };

  const navigateToDashboard = () => {
    switchWorkspace(null, SidebarContentType.AnalyticsDashboard);
    return navigate(`/${organizationId}/dashboard`);
  };

  const navigateToHomepage = () => {
    return navigate(`/${organizationId}`);
  };

  const navigateToWorkspace = (wId?: string, search = '') => {
    if (!wId) {
      const path =
        search.indexOf(`/${organizationId}`) === 0
          ? ''
          : `/${organizationId}/workspace`;
      navigate(`${path}${search}`);
      return;
    }
    setWorkspace(wId);
    navigate(
      `${links.getWorkspaceLink(wId)}${search.replace(links.getWorkspaceLink(wId), '')}`
    );
  };

  const navigateToCreateOverlay = () => {
    startNewOverlay();
  };

  const navigateToCreateOverlayInFolder = (
    _wsId?: string,
    folderId?: string
  ) => {
    startNewOverlay(folderId);
  };

  const navigateToRecorder = (folderId?: string) => {
    startClone({ folderId });
  };

  const navigateToRecorderFromHomepage = () => {
    startClone();
  };

  const navigateToTourShares = (tourId: number | string) => {
    navigateToTourPage(Number(tourId), 'links');
  };
  const navigateToFolder = (
    id: WorkspaceFolder['id'],
    wId: Workspace['id'],
    search = ''
  ) => {
    navigate(`${links.getFolderPageLink(id, wId)}${search}`);
  };

  const navigateToArchivedFolder = (
    id: WorkspaceFolder['id'],
    wId: Workspace['id'],
    search = ''
  ) => {
    navigate(`${links.getArchivedFolderPageLink(id, wId)}${search}`);
  };

  const navigateToPlaybookPage = (
    id: Playbook['id'],
    wId: Workspace['id'],
    tab = 'overview'
  ) => {
    navigate(links.getPlaybookPageLinkByWorkspace(id, wId, tab));
  };

  const navigateToPlaybookPageByWorkspace = (
    id: Playbook['id'],
    wId: Workspace['id'],
    tab = 'overview'
  ) => {
    navigate(links.getPlaybookPageLinkByWorkspace(id, wId, tab));
  };

  return {
    navigateToRoute,
    navigateToUpdateRecording,
    navigateToStudio,
    navigateToDemoPage,
    navigateToTourPage,
    navigateToTourPreview,
    navigateToAllTours,
    navigateToTourEditor,
    navigateToAllDemos,
    navigateToEditor,
    navigateToDemoPreview,
    navigateToClonePage,
    navigateToRecorder,
    navigateToRecorderFromHomepage,
    navigateToRecordingPreview,
    navigateToClonePageByWorkspace,
    navigateToTourPageByWorkspace,
    navigateToTourCapture,
    navigateToCreateOverlay,
    navigateToCreateOverlayInFolder,
    navigateToWorkspaces,
    navigateToWorkspace,
    navigateToDashboard,
    navigateToHomepage,
    navigateToDemoPageByWorkspace,
    navigateToPresentationPage,
    navigateToWebTourCapture,
    navigateToTourShares,
    navigateToPresentationsTab,
    navigateToPlaybookPresentationsTab,
    navigateToWebIntegrationSettings,
    navigateToPresentationPageFromHomepage,
    navigateToActivities,
    navigateToOrganizationSettings,
    updateBasePath,
    navigateToDemostacksTab,
    navigateToDemoShares,
    navigateToFolder,
    navigateToArchivedFolder,
    navigateToPlaybookPage,
    navigateToPlaybookPageByWorkspace,
    navigateToNotificationsSettings,
    navigateToWebAnalyticsDashboard,
    navigateToWebSettings,
    links,
  };
};
